import { detectCsvDelimiter, mergeSplitCurrencyCells, parseCsvLine } from "./csvParse";
import { escapeCsvCell } from "./csvFileHelpers";
import { parseCsvDateToIso, parseCsvDateToIsoOptional } from "./csvDate";
import { isCsvPlaceholderValue, parseOptionalFiniteNumber } from "./csvNumber";
import { normalizeEntityLabelsArray } from "./entityLabelFilter";
import { signedPercentClassName } from "./signedPercentFormat";

const RESERVED_ROW_KEYS = new Set(["_id", "cells", "labels", "rowKey"]);

/**
 * Parse a CSV into an in-memory table. Column labels come from the header row.
 * @param {string} csvText
 * @returns {{ columns: Array<{ id: string, label: string, type: "string"|"number"|"date", format: "text"|"number"|"percent"|"date" }>, rows: object[], keyTypes: Record<string, "string"|"number"|"date"> }}
 */
export function parseQuickAnalysisCsv(csvText) {
  const text = String(csvText ?? "").replace(/^\uFEFF/, "");
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  if (lines.length < 2) {
    throw new Error("CSV must have a header row and at least one data row.");
  }

  const sep = detectCsvDelimiter(text);
  const headerCells = parseCsvLine(lines[0], sep).map((cell) => cell.replace(/^"|"$/g, "").trim());
  if (headerCells.length === 0 || headerCells.every((cell) => cell === "")) {
    throw new Error("CSV header row has no column names.");
  }

  const columns = buildColumns(headerCells);
  const rawRows = [];
  for (let i = 1; i < lines.length; i++) {
    let cells = parseCsvLine(lines[i], sep).map((cell) => cell.replace(/^"|"$/g, "").trim());
    cells = mergeSplitCurrencyCells(cells);
    if (cells.length > columns.length) {
      throw new Error(
        `Row ${i + 1} has ${cells.length} values and the header has ${columns.length} columns.`
      );
    }
    while (cells.length < columns.length) cells.push("");
    if (cells.every((cell) => isCsvPlaceholderValue(cell))) continue;
    rawRows.push(cells);
  }

  if (rawRows.length === 0) {
    throw new Error("CSV has no data rows.");
  }

  columns.forEach((column, index) => {
    const values = rawRows.map((cells) => cells[index]);
    column.type = inferColumnType(values);
    column.format = inferColumnFormat(column.type, values);
  });

  const keyTypes = {};
  columns.forEach((column) => {
    keyTypes[column.id] = column.type;
  });

  const symbolColumn = findSymbolColumn(columns);
  const rows = rawRows.map((cells, index) => {
    const row = { _id: `qa-${index + 1}`, cells: {} };
    columns.forEach((column, colIndex) => {
      const display = cells[colIndex] ?? "";
      row.cells[column.id] = display;
      const sortValue = sortValueForCell(column.type, display);
      if (sortValue !== undefined) row[column.id] = sortValue;
    });
    row.rowKey = symbolColumn ? normalizeQuickAnalysisSymbol(row.cells[symbolColumn.id]) : "";
    row.labels = [];
    return row;
  });

  return { columns, rows, keyTypes };
}

/**
 * Symbol key for row labels. Matches server `normalizeSymbol` (trim + uppercase).
 * @param {unknown} value
 */
export function normalizeQuickAnalysisSymbol(value) {
  return String(value ?? "").trim().toUpperCase();
}

function findSymbolColumn(columns) {
  return (columns || []).find(
    (column) =>
      column &&
      (column.id === "symbol" || String(column.label || "").trim().toLowerCase() === "symbol")
  );
}

/**
 * Attach saved symbol labels onto parsed rows. Rows without a symbol stay unlabeled.
 * @param {object[]} rows
 * @param {Array<{ rowKey?: string, labels?: string[] }>} rowLabels
 */
export function applyQuickAnalysisRowLabels(rows, rowLabels) {
  const byKey = new Map();
  for (const entry of rowLabels || []) {
    const rowKey = normalizeQuickAnalysisSymbol(entry?.rowKey);
    if (!rowKey) continue;
    byKey.set(rowKey, normalizeEntityLabelsArray(entry?.labels));
  }
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const rowKey = normalizeQuickAnalysisSymbol(row?.rowKey);
    return {
      ...row,
      rowKey,
      labels: rowKey ? byKey.get(rowKey) || [] : [],
    };
  });
}

/**
 * Case-insensitive match against any cell.
 * @param {{ cells?: Record<string, string> }} row
 * @param {string} searchTerm
 */
export function quickAnalysisRowMatchesSearch(row, searchTerm) {
  const needle = String(searchTerm ?? "").trim().toLowerCase();
  if (!needle) return true;
  return Object.values(row?.cells || {}).some((cell) => String(cell ?? "").toLowerCase().includes(needle));
}

/**
 * Color a percent cell using the shared signed-percent classes. Other values stay plain.
 * @param {unknown} displayText
 * @returns {string}
 */
/**
 * CSV of selected rows using the columns passed in (visible order and original cell text).
 * @param {object[]} rows
 * @param {Array<{ id: string, label: string }>} columns
 */
export function buildQuickAnalysisCsvExportText(rows, columns) {
  const cols = (Array.isArray(columns) ? columns : []).filter((column) => column && column.id);
  const lines = [cols.map((column) => escapeCsvCell(column.label)).join(",")];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    lines.push(cols.map((column) => escapeCsvCell(row?.cells?.[column.id] ?? "")).join(","));
  });
  return lines.join("\n");
}

export function quickAnalysisPercentClassName(displayText) {
  const text = String(displayText ?? "").trim();
  if (!text.includes("%")) return "";
  const parsed = parseOptionalFiniteNumber(text);
  if (!parsed.ok || parsed.value == null || parsed.value === 0) return "";
  return signedPercentClassName(parsed.value > 0 ? "positive" : "negative");
}

function buildColumns(headerCells) {
  const usedIds = new Set();
  const labelCounts = new Map();
  return headerCells.map((raw, index) => {
    const trimmed = String(raw || "").trim();
    const baseLabel = trimmed || `Column ${index + 1}`;
    const seen = labelCounts.get(baseLabel) || 0;
    labelCounts.set(baseLabel, seen + 1);
    const label = seen === 0 ? baseLabel : `${baseLabel} (${seen + 1})`;
    const id = uniqueColumnId(slugColumnId(trimmed, index), usedIds);
    return { id, label, type: "string", format: "text" };
  });
}

/**
 * Advanced-filter field for one inferred column.
 * @param {{ id: string, label: string, type?: string, format?: string }} column
 */
export function quickAnalysisFilterField(column) {
  if (column?.format === "date" || column?.type === "date") {
    return { id: column.id, label: column.label, type: "date" };
  }
  if (column?.format === "percent") {
    return { id: column.id, label: column.label, type: "percent" };
  }
  if (column?.format === "number" || column?.type === "number") {
    return { id: column.id, label: column.label, type: "number" };
  }
  return { id: column.id, label: column.label, type: "text", placeholder: "Contains…" };
}

/**
 * @param {Array<{ id: string, label: string, type?: string, format?: string }>} columns
 */
export function buildQuickAnalysisFilterFields(columns) {
  return (Array.isArray(columns) ? columns : []).map(quickAnalysisFilterField);
}

/**
 * @param {Array<{ id: string, label: string, type?: string, format?: string }>} columns
 */
export function buildQuickAnalysisFilterMatchers(columns) {
  const matchers = {};
  (Array.isArray(columns) ? columns : []).forEach((column) => {
    if (!column?.id) return;
    matchers[column.id] = (row, value) => quickAnalysisCellMatchesFilter(column, row, value);
  });
  return matchers;
}

/**
 * @param {{ id: string, type?: string, format?: string }} column
 * @param {{ cells?: Record<string, string> }} row
 * @param {*} criteriaValue
 */
export function quickAnalysisCellMatchesFilter(column, row, criteriaValue) {
  if (!column?.id) return true;
  if (column.format === "date" || column.type === "date") {
    return dateInRange(row?.[column.id], criteriaValue);
  }
  if (column.format === "percent" || column.format === "number" || column.type === "number") {
    return numberInRange(row?.[column.id], criteriaValue);
  }
  const needle = String(criteriaValue ?? "").trim().toLowerCase();
  if (!needle) return true;
  return String(row?.cells?.[column.id] ?? "")
    .toLowerCase()
    .includes(needle);
}

function slugColumnId(label, index) {
  const base = String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || `column_${index + 1}`;
}

function uniqueColumnId(base, used) {
  let candidate = RESERVED_ROW_KEYS.has(base) ? `${base}_col` : base;
  let id = candidate;
  let n = 2;
  while (used.has(id)) {
    id = `${candidate}_${n}`;
    n += 1;
  }
  used.add(id);
  return id;
}

function inferColumnType(values) {
  const meaningful = values.filter((value) => !isCsvPlaceholderValue(value));
  if (meaningful.length === 0) return "string";
  if (meaningful.every((value) => parseCsvDateToIso(value).ok)) return "date";
  if (
    meaningful.every((value) => {
      const parsed = parseOptionalFiniteNumber(value);
      return parsed.ok && parsed.value != null;
    })
  ) {
    return "number";
  }
  return "string";
}

function inferColumnFormat(type, values) {
  if (type === "date") return "date";
  if (type !== "number") return "text";
  const meaningful = values.filter((value) => !isCsvPlaceholderValue(value));
  if (meaningful.length > 0 && meaningful.every((value) => String(value).includes("%"))) {
    return "percent";
  }
  return "number";
}

function filterBound(criteria, key) {
  if (!criteria || typeof criteria !== "object") return "";
  return String(criteria[key] ?? "").trim();
}

function dateInRange(iso, criteria) {
  const day = String(iso ?? "").trim();
  if (!day) return false;
  const from = filterBound(criteria, "from");
  const to = filterBound(criteria, "to");
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

function numberInRange(value, criteria) {
  const n = Number(value);
  if (!Number.isFinite(n)) return false;
  const minRaw = filterBound(criteria, "min");
  const maxRaw = filterBound(criteria, "max");
  if (minRaw !== "") {
    const min = Number(minRaw);
    if (!Number.isFinite(min) || n < min) return false;
  }
  if (maxRaw !== "") {
    const max = Number(maxRaw);
    if (!Number.isFinite(max) || n > max) return false;
  }
  return true;
}

function sortValueForCell(type, display) {
  if (type === "number") {
    const parsed = parseOptionalFiniteNumber(display);
    if (!parsed.ok || parsed.value == null) return undefined;
    return parsed.value;
  }
  if (type === "date") {
    const parsed = parseCsvDateToIsoOptional(display);
    if (!parsed.ok || !parsed.iso) return "";
    return parsed.iso;
  }
  return display == null ? "" : String(display);
}
